(() => {
  if (customElements.get('product-video-player')) return;

  class ProductVideoPlayer extends HTMLElement {
    connectedCallback() {
      if (this.ready) {
        this.connect();
        return;
      }
      this.ready = true;
      this.playLabel = this.dataset.playLabel || '';
      this.closeLabel = this.dataset.closeLabel || '';
      this.mode = this.dataset.mode === 'inline' ? 'inline' : 'floating';
      this.strip = this.querySelector('[data-pv-strip]');
      this.dock = this.querySelector('[data-pv-dock]');
      this.trigger = this.querySelector('[data-pv-trigger]');
      this.poster = this.querySelector('[data-pv-poster]');
      this.stage = this.querySelector('[data-pv-stage]');
      this.select = this.querySelector('[data-pv-select]');
      this.caption = this.querySelector('[data-pv-caption]');
      this.status = this.querySelector('[data-pv-status]');
      this.closeButton = this.querySelector('[data-pv-close]');
      this.templates = new Map([...this.querySelectorAll('template[data-pv-template]')]
        .map((node) => [String(node.dataset.pvTemplate), node]));
      try {
        const seen = new Set();
        this.sources = JSON.parse(this.querySelector('[data-pv-sources]')?.textContent || '[]')
          .filter((source) => {
            if (!/^\d+$/.test(String(source.id)) || !this.templates.has(String(source.id))) return false;
            if (!['video', 'external_video'].includes(source.kind)) return false;
            if (source.kind === 'external_video' && !['youtube', 'vimeo'].includes(source.host)) return false;
            const key = source.kind === 'external_video'
              ? `${source.host}:${source.externalId}` : `media:${source.id}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
      } catch (_) {
        this.sources = [];
      }
      if (!this.sources.length || !this.strip || !this.dock || !this.trigger || !this.stage) {
        this.hidden = true;
        return;
      }
      this.dock.classList.add('pv-player__dock');
      this.dock.dataset.mode = this.mode;
      this.strip.classList.add('pv-player__strip');
      this.strip.hidden = false;
      this.strip.dataset.pvOwner = this.dataset.productId || '';
      this.trigger.classList.add('pv-player__trigger');
      this.stage.classList.add('pv-player__stage');
      this.poster?.classList.add('pv-player__poster');
      this.closeButton?.setAttribute('aria-label', this.closeLabel);
      this.closeButton?.classList.add('pv-player__close');
      this.caption?.classList.add('pv-player__caption');
      this.status?.classList.add('pv-player__status');
      if (this.caption?.parentElement !== this.dock && this.caption?.parentElement?.contains(this.closeButton)) {
        this.caption.parentElement.classList.add('pv-player__header');
      }
      this.dock.hidden = false;
      this.stage.hidden = true;
      this.trigger.hidden = false;
      this.closed = false;
      this.buildStrip();
      this.buildSelect();
      const youtube = this.sources.findIndex((source) => source.host === 'youtube');
      this.setSource(youtube >= 0 ? youtube : 0);
      this.trigger.addEventListener('click', () => this.play());
      this.closeButton?.addEventListener('click', () => this.close());
      this.dock.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        }
      });
      this.select?.addEventListener('change', () => {
        const playAfterSelection = this.playing;
        this.setSource(Number(this.select.value));
        if (playAfterSelection) this.play();
      });
      this.onFrame = () => this.schedule();
      this.onShortcut = (event) => {
        const button = event.target.closest?.('[data-pv-strip] [data-pv-index]');
        if (!button || button.closest('[data-pv-strip]')?.dataset.pvOwner !== this.dataset.productId) return;
        this.playInGallery(Number(button.dataset.pvIndex));
      };
      this.connect();
    }

    disconnectedCallback() {
      this.disconnect();
      queueMicrotask(() => {
        if (!this.isConnected) {
          this.stop();
          this.strip?.remove();
          this.dock?.remove();
        }
      });
    }

    connect() {
      if (!this.sources?.length || this.connected) return;
      this.connected = true;
      window.addEventListener('scroll', this.onFrame, { passive: true });
      window.addEventListener('resize', this.onFrame, { passive: true });
      document.addEventListener('click', this.onShortcut);
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(document.body, { childList: true, subtree: true });
      this.mount();
      this.schedule();
    }

    disconnect() {
      this.connected = false;
      window.removeEventListener('scroll', this.onFrame);
      window.removeEventListener('resize', this.onFrame);
      document.removeEventListener('click', this.onShortcut);
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    schedule() {
      if (this.frame || !this.isConnected) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.mount();
        this.checkCollision();
      });
    }

    mount() {
      const gallery = document.querySelector('product-info media-gallery') || document.querySelector('media-gallery');
      if (gallery && (this.gallery !== gallery || !this.strip.isConnected)) {
        this.gallery = gallery;
        gallery.append(this.strip);
      }
      if (this.mode === 'inline') {
        const info = document.querySelector('[id^="ProductInfo-"]');
        if (info && this.dock.parentElement !== info) info.append(this.dock);
      } else if (this.dock.parentElement !== this) {
        this.append(this.dock);
      }
    }

    buildStrip() {
      this.strip.replaceChildren();
      this.sources.forEach((source, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pv-player__shortcut';
        button.dataset.pvIndex = String(index);
        button.setAttribute('aria-label', `${this.playLabel} ${index + 1}`);
        if (source.poster) {
          const image = document.createElement('img');
          image.src = source.poster;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 160;
          image.height = 90;
          button.append(image);
        }
        const label = document.createElement('span');
        label.textContent = `▶ ${this.playLabel} ${index + 1}`;
        button.append(label);
        this.strip.append(button);
      });
    }

    buildSelect() {
      if (!this.select) return;
      this.select.replaceChildren();
      this.sources.forEach((source, index) => {
        const option = document.createElement('option');
        option.value = String(index);
        option.textContent = `${this.playLabel} ${index + 1}`;
        this.select.append(option);
      });
      this.select.hidden = this.sources.length < 2;
      this.select.setAttribute('aria-label', this.playLabel);
    }

    setSource(index) {
      if (!Number.isInteger(index) || !this.sources[index]) return;
      this.stop();
      this.index = index;
      const source = this.sources[index];
      if (this.poster) {
        this.poster.hidden = !source.poster;
        if (source.poster) this.poster.src = source.poster;
        else this.poster.removeAttribute('src');
      }
      if (this.caption) this.caption.textContent = source.title || '';
      if (this.select) this.select.value = String(index);
      this.trigger.setAttribute('aria-label', `${this.playLabel} ${index + 1}`);
      this.strip.querySelectorAll('[data-pv-index]').forEach((button) => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.pvIndex) === index));
      });
      this.dock.dataset.pvMediaId = String(source.id);
      this.schedule();
    }

    playInGallery(index) {
      this.setSource(index);
      const source = this.sources[index];
      const gallery = document.querySelector('product-info media-gallery') || document.querySelector('media-gallery');
      const slide = [...(gallery?.querySelectorAll('[data-media-id]') || [])]
        .find((node) => node.dataset.mediaId?.endsWith(`-${source.id}`));
      const thumbnail = slide && [...(gallery?.querySelectorAll('[data-target]') || [])]
        .find((node) => node.dataset.target === slide.dataset.mediaId)?.querySelector('button');
      if (thumbnail) {
        thumbnail.click();
        if (this.status) this.status.textContent = `${this.playLabel} ${index + 1}`;
      } else if (gallery && slide && typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(slide.dataset.mediaId, false);
        if (this.status) this.status.textContent = `${this.playLabel} ${index + 1}`;
      } else {
        this.closed = false;
        this.dock.hidden = false;
        this.play();
      }
    }

    play() {
      const source = this.sources[this.index];
      const template = this.templates.get(String(source.id));
      if (!template) return;
      this.stop();
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      const fragment = template.content.cloneNode(true);
      const iframe = fragment.querySelector('iframe');
      if (iframe) {
        const url = new URL(iframe.getAttribute('src'), window.location.href);
        const allowed = ['www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com', 'youtube-nocookie.com', 'player.vimeo.com'];
        if (url.protocol !== 'https:' || !allowed.includes(url.hostname)) return;
        url.searchParams.set('autoplay', '1');
        iframe.src = url.toString();
        iframe.title = source.title || this.playLabel;
        iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
      }
      this.stage.replaceChildren(fragment);
      this.playing = true;
      this.closed = false;
      this.dock.hidden = false;
      this.dock.dataset.playing = 'true';
      this.dock.dataset.obstructed = 'false';
      this.trigger.hidden = true;
      this.stage.hidden = false;
      const video = this.stage.querySelector('video');
      if (video) {
        video.autoplay = false;
        video.loop = false;
        video.controls = true;
        video.playsInline = true;
        const promise = video.play();
        promise?.then(() => { this.dock.dataset.playback = 'playing'; })
          .catch(() => { this.dock.dataset.playback = 'user-control-required'; });
      } else {
        this.dock.dataset.playback = 'requested';
      }
      if (this.status) this.status.textContent = `${this.playLabel} ${this.index + 1}`;
      this.schedule();
    }

    stop() {
      this.stage?.querySelectorAll('video').forEach((video) => video.pause());
      this.stage?.replaceChildren();
      if (this.stage) this.stage.hidden = true;
      if (this.trigger) this.trigger.hidden = false;
      this.playing = false;
      if (this.dock) {
        this.dock.dataset.playing = 'false';
        delete this.dock.dataset.playback;
      }
    }

    close() {
      this.stop();
      this.closed = true;
      this.dock.hidden = true;
      if (this.dock.contains(document.activeElement)) {
        this.strip.querySelector(`[data-pv-index="${this.index}"]`)?.focus({ preventScroll: true });
      }
    }

    checkCollision() {
      if (this.mode !== 'floating' || this.closed || this.playing || this.dock.hidden) return;
      const box = this.dock.getBoundingClientRect();
      const protectedNodes = document.querySelectorAll(
        '[id^="ProductInfo-"] .price, [id^="ProductInfo-"] button, .shopify-payment-button, #whatsapp-widget-root a, cart-drawer.active'
      );
      const collision = [...protectedNodes].some((node) => {
        if (node === this.dock || this.dock.contains(node)) return false;
        const style = getComputedStyle(node);
        const rect = node.getBoundingClientRect();
        if (style.visibility === 'hidden' || style.display === 'none' || rect.width <= 0 || rect.height <= 0) return false;
        if (rect.bottom <= 0 || rect.top >= innerHeight || rect.right <= 0 || rect.left >= innerWidth) return false;
        return box.left < rect.right + 8 && box.right > rect.left - 8 && box.top < rect.bottom + 8 && box.bottom > rect.top - 8;
      });
      this.dock.dataset.obstructed = String(collision);
    }
  }

  customElements.define('product-video-player', ProductVideoPlayer);
})();
